import React, { useCallback, useEffect, useState } from "react";
import { X, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { sessionGet, sessionSet, purgeLegacyLocal } from "@/lib/sessionStore";

// Session-scoped dismissal: the pop-up shows again on the next browser session.
const STORAGE_KEY = "nc_announce_camps_2027_v2";

const CAMPS = [
  { slug: "san-diego", city: "San Diego", href: "https://www.sportingcampsandiego.us" },
  { slug: "arkansas", city: "Arkansas", href: "https://www.sportingcamparkansas.us" },
];

const COPY = {
  en: {
    kicker: "Registrations Open",
    titleTop: "Sporting CP Camps",
    titleBottom: "San Diego & Arkansas 2027",
    body: "Registrations are now open for both Sporting CP Soccer Camps in the USA. Secure your spot at the Early Bird rate until December 31, 2026.",
    dates: { "san-diego": "Point Loma Nazarene University · June 9–13, 2027", arkansas: "Springdale, AR · June 28 – July 2, 2027" },
    cta: "Register",
    close: "Close",
  },
  de: {
    kicker: "Anmeldung geöffnet",
    titleTop: "Sporting CP Camps",
    titleBottom: "San Diego & Arkansas 2027",
    body: "Die Anmeldungen für beide Sporting CP Soccer Camps in den USA sind jetzt geöffnet. Sichere dir deinen Platz zum Early-Bird-Preis bis 31. Dezember 2026.",
    dates: { "san-diego": "Point Loma Nazarene University · 9.–13. Juni 2027", arkansas: "Springdale, AR · 28. Juni – 2. Juli 2027" },
    cta: "Anmelden",
    close: "Schließen",
  },
  pt: {
    kicker: "Inscrições Abertas",
    titleTop: "Sporting CP Camps",
    titleBottom: "San Diego & Arkansas 2027",
    body: "As inscrições para os dois Sporting CP Soccer Camps nos EUA estão abertas. Garante o teu lugar com preço Early Bird até 31 de Dezembro de 2026.",
    dates: { "san-diego": "Point Loma Nazarene University · 9 a 13 de Junho de 2027", arkansas: "Springdale, AR · 28 de Junho a 2 de Julho de 2027" },
    cta: "Inscrever",
    close: "Fechar",
  },
};

const AnnouncementBanner = () => {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;
  const [open, setOpen] = useState(false);

  // Hook deps are complete: imports, module constants and state setters are stable.
  const markSeen = useCallback(() => sessionSet(STORAGE_KEY, "1"), []);
  const dismiss = useCallback(() => {
    setOpen(false);
    markSeen();
  }, [markSeen]);

  useEffect(() => {
    purgeLegacyLocal("nc_announce_sandiego_2027_v1", STORAGE_KEY);
    // Small delay so the pop-up doesn't compete with the initial page load.
    const timer = setTimeout(() => {
      if (sessionGet(STORAGE_KEY) !== "1") setOpen(true);
    }, 900);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  if (!open) return null;

  return (
    <div
      data-testid="announcement-banner"
      role="dialog"
      aria-modal="true"
      aria-labelledby="announce-title"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
    >
      <button
        type="button"
        aria-label={c.close}
        onClick={dismiss}
        className="absolute inset-0 bg-[#0F1B2D]/80 backdrop-blur-sm animate-fade-in"
        data-testid="announcement-backdrop"
      />
      <div className="relative w-full max-w-[560px] bg-[#0F1B2D] text-white border border-[#C4A46A]/40 shadow-2xl animate-slide-up">
        <button
          type="button"
          onClick={dismiss}
          aria-label={c.close}
          data-testid="announcement-close"
          className="absolute top-4 right-4 text-white/60 hover:text-[#C4A46A] transition-colors"
        >
          <X size={20} />
        </button>
        <div className="p-8 sm:p-10">
          <div className="overline text-[#C4A46A]">{c.kicker}</div>
          <h2
            id="announce-title"
            className="mt-4 font-display text-3xl sm:text-4xl tracking-tight leading-[1.05]"
          >
            <span className="block">{c.titleTop}</span>
            <span className="block">{c.titleBottom}</span>
          </h2>
          <span className="gold-rule mt-5 block" />
          <p className="mt-6 text-white/80 text-[15px] leading-relaxed">
            {c.body}
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {CAMPS.map((camp) => (
              <a
                key={camp.slug}
                href={camp.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={markSeen}
                data-testid={`announcement-cta-${camp.slug}`}
                className="group flex flex-col justify-between gap-4 p-5 border border-[#C4A46A]/40 hover:bg-[#C4A46A] hover:text-[#0F1B2D] transition-colors"
              >
                <div>
                  <div className="font-display text-2xl">{camp.city}</div>
                  <div className="mt-1 text-[12px] text-white/60 group-hover:text-[#0F1B2D]/80 leading-snug">
                    {c.dates[camp.slug]}
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#C4A46A] group-hover:text-[#0F1B2D]">
                  {c.cta} <ArrowUpRight size={14} />
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
